import type {RepositoryNWO} from '@github-ui/current-repository'
import {tagPath} from '@github-ui/paths'
import {KebabHorizontalIcon, TagIcon} from '@primer/octicons-react'
import {ActionList, IconButton, Link} from '@primer/react'
import {Octicon} from '@primer/react/deprecated'
import {Dialog} from '@primer/react/experimental'
import {clsx} from 'clsx'
import {useCallback, useRef, useState} from 'react'

import type {BranchCommitState} from '../../../hooks/use-load-branch-commits'

export interface CommitTagInfoProps {
  data: BranchCommitState
  repo: RepositoryNWO
  className?: string
}

export function CommitTagInfo({data, repo, className}: CommitTagInfoProps) {
  if (data.tags.length === 0) {
    return null
  }

  // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
  const lastTag = data.tags[data.tags.length - 1]!

  const renderTagsInLine = data.tags.length > 2 && data.tags.length < 25
  const renderTagsInDialog = data.tags.length >= 25

  return (
    <div className={clsx(className, 'd-flex flex-items-center flex-wrap gap-1')}>
      <Octicon className="mr-2 fgColor-muted" icon={TagIcon} />

      {/* eslint-disable-next-line @typescript-eslint/no-non-null-assertion */}
      <Link href={tagPath({owner: repo.ownerLogin, repo: repo.name, tag: data.tags[0]!})} className="fgColor-default">
        {data.tags[0]}
      </Link>

      {renderTagsInLine ? <ExpandedTags tags={data.tags} repo={repo} /> : null}
      {renderTagsInDialog ? <CommitTagsDialog tags={data.tags} repo={repo} /> : null}

      {data.tags.length > 1 ? (
        <Link href={tagPath({owner: repo.ownerLogin, repo: repo.name, tag: lastTag})} className="fgColor-muted">
          {lastTag}
        </Link>
      ) : null}
    </div>
  )
}

function ExpandedTags({tags, repo}: {tags: string[]; repo: RepositoryNWO}) {
  const [tagsExpanded, setTagsExpanded] = useState(false)
  const firstExpandedTagRef = useRef<HTMLAnchorElement>(null)

  const onExpand = useCallback(() => {
    setTagsExpanded(true)
    requestAnimationFrame(() => firstExpandedTagRef.current?.focus())
  }, [])

  return tagsExpanded ? (
    <>
      {tags.slice(1, tags.length - 1).map((tag, index) => {
        return (
          <Link
            ref={index === 0 ? firstExpandedTagRef : null}
            href={tagPath({owner: repo.ownerLogin, repo: repo.name, tag})}
            className="fgColor-muted mx-1"
            key={tag}
          >
            {tag}
          </Link>
        )
      })}
    </>
  ) : (
    <IconButton
      aria-label="Show more tags"
      tooltipDirection="s"
      icon={KebabHorizontalIcon}
      size="small"
      variant="invisible"
      onClick={onExpand}
    />
  )
}

function CommitTagsDialog({tags, repo}: {tags: string[]; repo: RepositoryNWO}) {
  const [dialogOpen, setDialogOpen] = useState(false)
  const returnFocusRef = useRef(null)

  return (
    <>
      <IconButton
        aria-label="Show all tags"
        tooltipDirection="s"
        icon={KebabHorizontalIcon}
        size="small"
        variant="invisible"
        onClick={() => setDialogOpen(true)}
        ref={returnFocusRef}
      />
      {dialogOpen && (
        <Dialog
          title={`${tags.length} tags`}
          onClose={() => setDialogOpen(false)}
          returnFocusRef={returnFocusRef}
          width="medium"
          height={tags.length >= 12 ? 'small' : 'auto'}
          renderBody={() => {
            return (
              <ActionList className="overflow-y-auto py-2" data-testid="tag-dialog-list">
                {tags.map(tag => (
                  <ActionList.LinkItem key={tag} href={tagPath({owner: repo.ownerLogin, repo: repo.name, tag})}>
                    {tag}
                  </ActionList.LinkItem>
                ))}
              </ActionList>
            )
          }}
        />
      )}
    </>
  )
}
